import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams, NavLink } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/lang";
import { useSeo } from "../lib/seo";
import { api, fmtUSD, STATUS_LABELS, STATUS_ORDER } from "../lib/api";

function Tracker({ status, lang }) {
  const idx = STATUS_ORDER.indexOf(status);
  return (
    <div className="tracker" data-testid="status-tracker">
      {STATUS_ORDER.map((s, i) => {
        const cls = i < idx ? "done" : i === idx ? "active" : "";
        const label = STATUS_LABELS[s][lang] || STATUS_LABELS[s].en;
        return (
          <div className={`tracker-step ${cls}`} key={s} data-testid={`tracker-${s}`}>
            <span className="ts-num">{String(i + 1).padStart(2, "0")}</span>
            <span className="ts-label">{label}</span>
            <span className="ts-state">{cls === "done" ? "✓" : cls === "active" ? "•" : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

export function DashboardHome() {
  const { user, loading: authLoading, logout } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  useSeo({ title: "Dashboard — Brand and Behavior", noindex: true });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login?next=/dashboard", { replace: true });
      return;
    }
    api.get("/orders").then((r) => { setOrders(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [authLoading, user, navigate]);

  if (authLoading || !user) {
    return <main className="app-main"><section className="page-hero"><h1>{t("Loading…", "Cargando…")}</h1></section></main>;
  }

  return (
    <main className="app-main" data-testid="dashboard-home">
      <section className="dash-header">
        <Link to="/" className="back-link" data-testid="back-to-home">← {t("Back to home", "Volver al inicio")}</Link>
        <div className="h-greet">{t("Hello, ", "Hola, ")}<em className="it">{user?.name?.split(" ")[0] || ""}</em>.</div>
        <div className="h-email">{user?.email}</div>
        <div style={{ marginTop: 14 }}>
          <button onClick={async () => { await logout(); navigate("/"); }} className="button" data-testid="logout-btn">{t("Sign out", "Cerrar sesión")}</button>
        </div>
      </section>

      <div className="dash-tabs">
        <NavLink to="/dashboard" end className={({ isActive }) => isActive ? "active" : ""}>{t("Projects", "Proyectos")}</NavLink>
        <NavLink to="/dashboard/requests" className={({ isActive }) => isActive ? "active" : ""}>{t("Requests", "Requests")}</NavLink>
      </div>

      <section style={{ paddingTop: 6 }}>
        {loading ? <div className="empty">{t("Loading…", "Cargando…")}</div> :
          orders.length === 0 ? (
            <div className="empty" data-testid="no-orders">
              {t("No projects yet. ", "Sin proyectos aún. ")}<Link to="/services">{t("Browse services", "Ver servicios")}</Link>.
            </div>
          ) : (
            <div>
              {orders.map((o) => (
                <Link to={`/dashboard/orders/${o.order_id}`} className="order-row" key={o.order_id} data-testid={`order-${o.order_id}`}>
                  <div>
                    <div className="meta">{o.service_slug}</div>
                    <div className="title">{o.service_name}</div>
                  </div>
                  <div className="meta">{(STATUS_LABELS[o.status]?.[lang]) || o.status}</div>
                  <div className="meta">{fmtUSD(o.amount)}</div>
                  <div><span className={`status-pill ${o.payment_status === "paid" ? "paid" : ""}`}>{o.payment_status}</span></div>
                </Link>
              ))}
            </div>
          )
        }
      </section>
    </main>
  );
}

export function OrderDetail() {
  const { orderId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useLang();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useSeo({ title: "Order — Brand and Behavior", noindex: true });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/login?next=${encodeURIComponent(`/dashboard/orders/${orderId}`)}`, { replace: true }); return; }
    api.get(`/orders/${orderId}`)
      .then((r) => { setOrder(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [authLoading, user, orderId, navigate]);

  if (authLoading || !user || loading) return <main className="app-main"><section className="page-hero"><h1>{t("Loading…", "Cargando…")}</h1></section></main>;
  if (!order) return <main className="app-main"><section className="page-hero"><h1>{t("Order not found", "Pedido no encontrado")}</h1></section></main>;

  return (
    <main className="app-main" data-testid="order-detail">
      <section className="page-hero">
        <Link to="/dashboard" className="back-link" data-testid="back-to-dashboard">← {t("Back to dashboard", "Volver al panel")}</Link>
        <div className="kicker">{order.service_slug}</div>
        <h1>{order.service_name}</h1>
        <p>{fmtUSD(order.amount)} · {order.payment_status === "paid" ? t("Paid", "Pagado") : order.payment_status}</p>
      </section>

      <section style={{ padding: "0 22px 24px" }}>
        <h2 className="section-h" style={{ marginTop: 24 }}>{t("Status", "Estado")}</h2>
        <Tracker status={order.status} lang={lang} />
        {order.delivery_estimate && (
          <DeliveryCountdown deliveryEstimate={order.delivery_estimate} t={t} status={order.status} />
        )}
      </section>

      {order.payment_status === "paid" && (
        <section style={{ padding: "0 22px 24px" }}>
          <h2 className="section-h">{t("Files", "Archivos")}</h2>
          <OrderFilesPanel orderId={order.order_id} t={t} userEmail={user.email} />
        </section>
      )}

      <section style={{ padding: "0 22px 24px" }}>
        <h2 className="section-h">{t("Messages", "Mensajes")}</h2>
        <OrderMessages orderId={order.order_id} userEmail={user.email} t={t} />
      </section>

      {order.needs_clarification && (
        <section style={{ padding: "0 22px 24px" }}>
          <div className="banner-warning">
            <strong>{t("Needs clarification: ", "Necesita clarificación: ")}</strong>{order.needs_clarification}
          </div>
        </section>
      )}

      {order.deliverable_url && (
        <section style={{ padding: "0 22px 24px" }}>
          <h2 className="section-h">{t("Deliverable", "Entregable")}</h2>
          <p><a href={order.deliverable_url} target="_blank" rel="noopener noreferrer" className="button" data-testid="deliverable-link">{t("Open deliverable ↗", "Abrir entregable ↗")}</a></p>
          {order.deliverable_notes && <p style={{ marginTop: 12, color: "var(--muted)", maxWidth: 760 }}>{order.deliverable_notes}</p>}
        </section>
      )}

      {order.next_step_recommendation && (
        <section style={{ padding: "0 22px 80px" }}>
          <h2 className="section-h">{t("Recommended next step", "Próximo paso recomendado")}</h2>
          <p style={{ fontSize: 18, lineHeight: 1.45, maxWidth: 760 }}>{order.next_step_recommendation}</p>
        </section>
      )}

      {order.payment_status === "paid" && order.status === "intake_pending" && (
        <section style={{ padding: "0 22px 80px" }}>
          <button className="button" onClick={() => navigate(`/intake/${order.order_id}`)} data-testid="open-intake">{t("Complete intake", "Completar cuestionario")}</button>
        </section>
      )}
    </main>
  );
}

export function RequestsList() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [reqs, setReqs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useSeo({ title: "Requests — Brand and Behavior", noindex: true });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/dashboard/requests", { replace: true }); return; }
    Promise.all([api.get("/requests"), api.get("/orders")]).then(([rr, ro]) => {
      setReqs(rr.data); setOrders(ro.data.filter((o) => o.service_slug === "brand-partner" && o.payment_status === "paid"));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [authLoading, user, navigate]);

  if (authLoading || !user) {
    return <main className="app-main"><section className="page-hero"><h1>{t("Loading…", "Cargando…")}</h1></section></main>;
  }

  return (
    <main className="app-main" data-testid="requests-list">
      <section className="dash-header">
        <div className="h-greet">{t("Requests", "Requests")}</div>
        <div className="h-email">{t("Brand Partner only. Max 4/month, 1 active.", "Solo Brand Partner. Máx 4/mes, 1 activo.")}</div>
      </section>
      <div className="dash-tabs">
        <NavLink to="/dashboard" end className={({ isActive }) => isActive ? "active" : ""}>{t("Projects", "Proyectos")}</NavLink>
        <NavLink to="/dashboard/requests" className={({ isActive }) => isActive ? "active" : ""}>{t("Requests", "Requests")}</NavLink>
      </div>
      <section style={{ padding: "22px" }}>
        {orders.length === 0
          ? <div className="empty">{t("Activate Brand Partner to submit requests.", "Activa Brand Partner para enviar requests.")} <Link to="/services/brand-partner">→</Link></div>
          : <Link to="/dashboard/requests/new" className="button" data-testid="new-request-btn">{t("New request", "Nuevo request")}</Link>
        }
      </section>
      <section>
        {loading ? <div className="empty">{t("Loading…", "Cargando…")}</div> :
          reqs.length === 0 ? <div className="empty">{t("No requests yet.", "Aún no hay requests.")}</div> :
          reqs.map((r) => (
            <div className="order-row" key={r.request_id} data-testid={`request-${r.request_id}`}>
              <div>
                <div className="meta">{r.request_type} · {r.priority}</div>
                <div className="title">{r.title}</div>
              </div>
              <div className="meta">{r.status}</div>
              <div className="meta">{new Date(r.created_at).toLocaleDateString()}</div>
              <div>{r.admin_response ? <span className="status-pill paid">✓</span> : <span className="status-pill">…</span>}</div>
            </div>
          ))
        }
      </section>
    </main>
  );
}

export function NewRequest() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({ order_id: "", title: "", request_type: "Strategic review", context: "", assets: "", decision_needed: "", deadline: "", priority: "normal" });
  const [err, setErr] = useState(""); const [submitting, setSubmitting] = useState(false);
  useSeo({ title: "New request — Brand and Behavior", noindex: true });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/dashboard/requests/new", { replace: true }); return; }
    api.get("/orders").then((r) => {
      const eligible = r.data.filter((o) => o.service_slug === "brand-partner" && o.payment_status === "paid");
      setOrders(eligible);
      if (eligible[0]) setForm((f) => ({ ...f, order_id: eligible[0].order_id }));
    });
  }, [authLoading, user, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSubmitting(true);
    try {
      await api.post("/requests", form);
      navigate("/dashboard/requests");
    } catch (e) {
      setErr(e?.response?.data?.detail || t("Could not submit request.", "No se pudo enviar el request."));
    } finally { setSubmitting(false); }
  };

  return (
    <main className="app-main" data-testid="new-request-page">
      <section className="page-hero">
        <div className="kicker">{t("Brand Partner", "Brand Partner")} · {t("New request", "Nuevo request")}</div>
        <h1><em className="it">{t("Give us the context", "Danos el contexto")}</em>.</h1>
        <p>{t("Requests without enough context get marked 'Needs clarification' and don't count against your monthly limit until clarified.", "Los requests sin contexto suficiente se marcan 'Necesita clarificación' y no cuentan contra tu límite mensual hasta clarificarse.")}</p>
      </section>
      {orders.length === 0
        ? <section style={{ padding: 22 }}><div className="empty">{t("No active Brand Partner subscription.", "Sin suscripción activa de Brand Partner.")}</div></section>
        : (
          <form className="form-card" onSubmit={submit}>
            <div className="form-section">
              <div className="head"><h3>{t("Request", "Request")}</h3></div>
              <div className="body">
                <div className="form-row"><label>{t("Order", "Pedido")}</label>
                  <select value={form.order_id} onChange={(e) => set("order_id", e.target.value)}>
                    {orders.map((o) => <option key={o.order_id} value={o.order_id}>{o.service_name} · {o.order_id}</option>)}
                  </select>
                </div>
                <div className="form-row"><label>{t("Title", "Título")}</label><input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="req-title" /></div>
                <div className="form-row"><label>{t("Request type", "Tipo de request")}</label>
                  <select value={form.request_type} onChange={(e) => set("request_type", e.target.value)}>
                    <option>Strategic review</option>
                    <option>Messaging feedback</option>
                    <option>Website / deck / content review</option>
                    <option>Designer / dev direction</option>
                    <option>Campaign direction</option>
                    <option>Roadmap / priorities</option>
                  </select>
                </div>
                <div className="form-row"><label>{t("Context", "Contexto")}</label><textarea required value={form.context} onChange={(e) => set("context", e.target.value)} data-testid="req-context" /></div>
                <div className="form-row"><label>{t("Assets / links", "Assets / links")}</label><textarea value={form.assets} onChange={(e) => set("assets", e.target.value)} /></div>
                <div className="form-row"><label>{t("Decision needed", "Decisión que necesitas")}</label><textarea required value={form.decision_needed} onChange={(e) => set("decision_needed", e.target.value)} data-testid="req-decision" /></div>
                <div className="form-row"><label>{t("Deadline (optional)", "Deadline (opcional)")}</label><input type="date" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} /></div>
                <div className="form-row"><label>{t("Priority", "Prioridad")}</label>
                  <select value={form.priority} onChange={(e) => set("priority", e.target.value)}>
                    <option value="normal">Normal</option>
                    <option value="urgent">Urgent (add-on may apply)</option>
                  </select>
                </div>
              </div>
            </div>
            {err && <div className="banner-warning">{err}</div>}
            <div className="form-actions"><button type="submit" disabled={submitting} className="button" data-testid="submit-request">{submitting ? t("Submitting…", "Enviando…") : t("Send request", "Enviar request")}</button></div>
          </form>
        )
      }
    </main>
  );
}

function DeliveryCountdown({ deliveryEstimate, t, status }) {
  const date = new Date(deliveryEstimate);
  const now = new Date();
  const diffMs = date - now;
  const businessDaysRemaining = (() => {
    if (diffMs <= 0) return 0;
    let count = 0;
    const cursor = new Date(now);
    while (cursor < date) {
      cursor.setDate(cursor.getDate() + 1);
      if (cursor.getDay() !== 0 && cursor.getDay() !== 6) count += 1;
    }
    return count;
  })();
  const delivered = status === "final_delivery" || status === "next_step_recommended";
  const formatted = date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return (
    <div className="delivery-countdown" data-testid="delivery-countdown">
      <span className="dc-label">{delivered ? t("Delivered", "Entregado") : t("Estimated delivery", "Entrega estimada")}</span>
      <span className="dc-date">{formatted}</span>
      {!delivered && diffMs > 0 && (
        <span className="dc-rem">{businessDaysRemaining} {t("business day(s) remaining", "día(s) hábil(es) restante(s)")}</span>
      )}
      {!delivered && diffMs <= 0 && (
        <span className="dc-rem dc-late">{t("In final review", "En revisión final")}</span>
      )}
    </div>
  );
}

function OrderMessages({ orderId, userEmail, t }) {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/messages`);
      setMsgs(data);
    } catch (e) { console.warn("Messages refresh failed:", e); }
  };
  useEffect(() => { refresh(); }, [orderId, refresh]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/orders/${orderId}/messages`, { body: body.trim() });
      setBody("");
      await refresh();
    } catch (err) { console.error("Send failed:", err); }
    finally { setSending(false); }
  };

  return (
    <div className="messages-panel" data-testid="messages-panel">
      <div className="messages-list">
        {msgs.length === 0 && <p className="messages-empty">{t("No messages yet. Send the first one below.", "Aún no hay mensajes. Envía el primero abajo.")}</p>}
        {msgs.map((m) => (
          <div key={m.message_id} className={`msg-row ${m.author_email === userEmail ? "msg-mine" : "msg-theirs"}`} data-testid={`msg-${m.message_id}`}>
            <div className="msg-meta">
              <span className="msg-author">{m.author_role === "admin" ? "Brand & Behavior" : m.author_email}</span>
              <span className="msg-date">{new Date(m.created_at).toLocaleDateString()} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
            <div className="msg-body">{m.body}</div>
          </div>
        ))}
      </div>
      <form className="messages-input" onSubmit={send}>
        <textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t("Send a message to Brand & Behavior…", "Envía un mensaje a Brand & Behavior…")}
          data-testid="msg-input"
          disabled={sending}
        />
        <button type="submit" className="button" disabled={sending || !body.trim()} data-testid="msg-send">{sending ? t("Sending…", "Enviando…") : t("Send", "Enviar")}</button>
      </form>
    </div>
  );
}

function OrderFilesPanel({ orderId, t, userEmail }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/files`);
      setFiles(data);
    } catch (e) { void e; }
  }, [orderId]);

  useEffect(() => { refresh(); }, [refresh]);

  const onPick = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setErr(""); setUploading(true);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/orders/${orderId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e2) {
        setErr(`${f.name}: ${e2?.response?.data?.detail || e2.message}`);
      }
    }
    setUploading(false);
    e.target.value = "";
    await refresh();
  };

  const download = async (file) => {
    try {
      const resp = await api.get(`/orders/${orderId}/files/${file.file_id}`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: file.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { void e; }
  };

  const remove = async (fileId) => {
    try { await api.delete(`/orders/${orderId}/files/${fileId}`); await refresh(); }
    catch (e) { void e; }
  };

  const mine = files.filter((f) => f.user_email === userEmail);
  const theirs = files.filter((f) => f.user_email !== userEmail);

  return (
    <div className="files-panel" data-testid="files-panel">
      <div className="files-upload-zone">
        <label className="upload-btn" data-testid="dashboard-upload-btn">
          <input type="file" multiple onChange={onPick} disabled={uploading} style={{ display: "none" }}
            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.ai,.psd,.fig,.sketch,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.md,.zip" />
          {uploading ? t("Uploading…", "Subiendo…") : t("Upload materials", "Subir materiales")}
        </label>
        <span className="upload-hint">{t("JPG · PNG · PDF · AI · PSD · DOC · PPT · ZIP · up to 25 MB each", "JPG · PNG · PDF · AI · PSD · DOC · PPT · ZIP · hasta 25 MB cada uno")}</span>
      </div>
      {err && <div className="banner-warning" style={{ marginTop: 12 }}>{err}</div>}

      {theirs.length > 0 && (
        <div className="files-block" data-testid="files-from-team">
          <div className="files-block-title">{t("Delivered by Brand & Behavior", "Entregado por Brand & Behavior")}</div>
          <ul className="file-list">
            {theirs.map((f) => (
              <li key={f.file_id} className="file-row file-row-deliverable">
                <span className="file-name">📦 {f.original_filename}</span>
                <span className="file-size">{(f.size / 1024).toFixed(0)} KB · {new Date(f.created_at).toLocaleDateString()}</span>
                <button type="button" className="file-remove" onClick={() => download(f)} data-testid={`dl-${f.file_id}`}>{t("Download", "Descargar")}</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="files-block" data-testid="files-from-me">
        <div className="files-block-title">{t("Your uploads", "Tus subidas")}</div>
        {mine.length === 0 ? (
          <p className="files-empty">{t("No materials uploaded yet.", "Aún no has subido materiales.")}</p>
        ) : (
          <ul className="file-list">
            {mine.map((f) => (
              <li key={f.file_id} className="file-row">
                <span className="file-name">{f.original_filename}</span>
                <span className="file-size">{(f.size / 1024).toFixed(0)} KB · {new Date(f.created_at).toLocaleDateString()}</span>
                <button type="button" className="file-remove" onClick={() => download(f)}>{t("Download", "Descargar")}</button>
                <button type="button" className="file-remove" onClick={() => remove(f.file_id)}>{t("Remove", "Eliminar")}</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

